import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon, NoSymbolIcon } from '@heroicons/react/24/outline';

/**
 * CompactFileReceiver Component
 * A minimal dropzone for tight spaces like the FianzaView document grid
 *
 * Variants:
 * - "box" (default): Small box with dashed border for grids
 * - "inline": Button-like appearance for inline use
 */
const CompactFileReceiver = ({
  onFilesChange,
  maxFiles = 1,
  disabled = false,
  type = 'pdf',
  placeholder = null, // Custom placeholder text, auto-generated if null
  variant = 'box', // 'box' or 'inline'
}) => {
  const onDrop = useCallback((droppedFiles) => {
    if (disabled) return;

    const filesToAdd = droppedFiles.slice(0, maxFiles);

    if (filesToAdd.length > 0) {
      onFilesChange?.(filesToAdd);
    }
  }, [maxFiles, disabled, onFilesChange]);

  const getAcceptedFileTypes = () => {
    switch (type) {
      case 'excel':
        return {
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-excel': ['.xls']
        };
      case 'xml':
        return {
          'application/xml': ['.xml'],
          'text/xml': ['.xml']
        };
      case 'image-or-pdf':
        return {
          'application/pdf': ['.pdf'],
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/png': ['.png'],
          'image/gif': ['.gif'],
          'image/webp': ['.webp'],
          'image/bmp': ['.bmp'],
          'image/tiff': ['.tif', '.tiff'],
          'image/heic': ['.heic'],
          'image/heif': ['.heif']
        };
      case 'pdf':
      default:
        return {
          'application/pdf': ['.pdf']
        };
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    maxFiles: maxFiles,
    accept: getAcceptedFileTypes(),
    disabled,
  });

  const getShortTypeLabel = () => {
    switch (type) {
      case 'excel':
        return '.xlsx';
      case 'xml':
        return '.xml';
      case 'image-or-pdf':
        return 'PDF/Img';
      case 'pdf':
      default:
        return '.pdf';
    }
  };

  // Inline variant - compact dropzone that fits inline
  if (variant === 'inline') {
    // Keep text constant to prevent width changes
    const displayText = placeholder || 'Arrastra o haz clic';

    return (
      <div
        {...getRootProps()}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border-2 border-dashed transition-colors duration-150 cursor-pointer
          ${disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : isDragReject
            ? 'border-red-400 bg-red-50'
            : isDragActive
            ? 'border-azul-surelia bg-azul-surelia/10'
            : 'border-gray-300 bg-gray-50 hover:border-azul-surelia/50 hover:bg-white'
          }`}
      >
        <input {...getInputProps()} />
        {disabled ? (
          <NoSymbolIcon className="w-3.5 h-3.5 text-gray-400" />
        ) : isDragReject ? (
          <NoSymbolIcon className="w-3.5 h-3.5 text-red-400" />
        ) : (
          <CloudArrowUpIcon className={`w-3.5 h-3.5 transition-colors ${
            isDragActive ? 'text-azul-surelia' : 'text-gray-400'
          }`} />
        )}
        <span className={`text-xs transition-colors ${
          disabled ? 'text-gray-400' :
          isDragReject ? 'text-red-500' :
          isDragActive ? 'text-azul-surelia font-medium' : 'text-gray-500'
        }`}>
          {disabled ? 'No disponible' : displayText}
        </span>
      </div>
    );
  }

  // Box variant (default) - dashed border for grids
  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded p-2 transition-colors duration-150 cursor-pointer
        ${disabled
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
          : isDragReject
          ? 'border-red-400 bg-red-50'
          : isDragActive
          ? 'border-azul-surelia bg-azul-surelia/5'
          : 'border-gray-300 hover:border-azul-surelia/50 hover:bg-gray-50'
        }`}
    >
      <input {...getInputProps()} />
      <div className="flex items-center justify-center gap-1.5">
        {disabled ? (
          <NoSymbolIcon className="w-4 h-4 text-gray-300" />
        ) : isDragReject ? (
          <NoSymbolIcon className="w-4 h-4 text-red-400" />
        ) : (
          <CloudArrowUpIcon className={`w-4 h-4 transition-colors ${
            isDragActive ? 'text-azul-surelia' : 'text-gray-400'
          }`} />
        )}
        <span className={`text-[10px] transition-colors ${
          disabled ? 'text-gray-400' :
          isDragReject ? 'text-red-500' :
          isDragActive ? 'text-azul-surelia font-medium' : 'text-gray-500'
        }`}>
          {disabled
            ? 'Máximo'
            : isDragReject
            ? 'Inválido'
            : isDragActive
            ? 'Soltar'
            : placeholder || `Subir ${getShortTypeLabel()}`
          }
        </span>
      </div>
    </div>
  );
};

export default CompactFileReceiver;
